import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MdEmail } from 'react-icons/md';
import PublicBrandHeader from '../Layout/PublicBrandHeader';
import './ForgotPassword.css';

const ForgotPassword = () => {
    const [formData, setFormData] = useState({
        email: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const formRef = useRef(null);
    const successHeadingRef = useRef(null);

    useEffect(() => {
        if (success && successHeadingRef.current) {
            successHeadingRef.current.focus();
        }
    }, [success]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        setError('');
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.email.trim()) {
            errors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post('/api/auth/forgot-password', {
                email: formData.email
            });

            if (response.data.message) {
                setSuccess(true);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred. Please try again.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkipToForm = (e) => {
        e.preventDefault();
        if (formRef.current) {
            const firstInput = formRef.current.querySelector('input');
            if (firstInput) {
                firstInput.focus();
            }
        }
    };

    return (
        <>
            {!success && (
                <a href="#forgot-password-form" className="skip-link" onClick={handleSkipToForm}>
                    Skip to forgot password form
                </a>
            )}
            <PublicBrandHeader />
            <div className="forgot-password-container">
                <div className="forgot-password-card">
                    {!success && (
                        <Link to="/login" className="forgot-password-back-link">
                            <span className="back-arrow" aria-hidden="true">←</span>
                            Back to Login
                        </Link>
                    )}

                    <div className="forgot-password-form-container">
                        {success ? (
                            <>
                                <h1
                                    ref={successHeadingRef}
                                    className="forgot-password-title"
                                    tabIndex={-1}
                                >
                                    Check Your Email
                                </h1>
                                <p className="forgot-password-subtitle">
                                    If an account exists for{' '}
                                    <span className="forgot-password-email-highlight">
                                        {formData.email}
                                    </span>
                                    , we sent a password reset link.
                                </p>

                                <div
                                    className="forgot-password-success-message"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <p>
                                        Please check your inbox and click the link to reset your password.
                                    </p>
                                    <p className="forgot-password-check-spam">
                                        If you don&apos;t see the email, please check your spam folder.
                                    </p>
                                </div>

                                <Link
                                    to="/login"
                                    className="forgot-password-submit-button forgot-password-back-to-login"
                                >
                                    Back to Login
                                </Link>
                            </>
                        ) : (
                            <>
                                <h1 className="forgot-password-title">Forgot Password</h1>
                                <p className="forgot-password-subtitle">
                                    Enter your email address and we&apos;ll send you a link to reset your password.
                                </p>

                                {error && (
                                    <div
                                        id="error-message"
                                        className="forgot-password-error-message"
                                        role="alert"
                                        aria-live="assertive"
                                    >
                                        {error}
                                    </div>
                                )}

                                <form
                                    ref={formRef}
                                    id="forgot-password-form"
                                    onSubmit={handleSubmit}
                                    className="forgot-password-form"
                                    noValidate
                                >
                                    <div className="forgot-password-form-group">
                                        <label htmlFor="email" className="forgot-password-form-label">
                                            Email
                                        </label>
                                        <div className="forgot-password-input-container">
                                            <MdEmail className="forgot-password-input-icon" aria-hidden="true" />
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="Email"
                                                className={`forgot-password-form-input ${validationErrors.email ? 'error' : ''}`}
                                                required
                                                autoComplete="email"
                                                aria-invalid={validationErrors.email ? 'true' : 'false'}
                                                aria-describedby={
                                                    validationErrors.email
                                                        ? 'email-error'
                                                        : error
                                                            ? 'error-message'
                                                            : undefined
                                                }
                                            />
                                        </div>
                                        {validationErrors.email && (
                                            <div
                                                id="email-error"
                                                className="forgot-password-field-error"
                                                role="alert"
                                            >
                                                {validationErrors.email}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="forgot-password-submit-button"
                                        disabled={isLoading}
                                        aria-describedby={isLoading ? 'loading-message' : undefined}
                                    >
                                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>

                                    {isLoading && (
                                        <div
                                            id="loading-message"
                                            className="forgot-password-sr-only"
                                            aria-live="polite"
                                        >
                                            Sending reset link, please wait...
                                        </div>
                                    )}
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;
